import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Document, CrewMember } from '../types';
import { documentService } from '../services/documentService';
import { crewService } from '../services/crewService';

/**
 * Document List Page — displays uploaded documents in a table format
 * with crew name, document type, issuer, issue date, and expiry date.
 *
 * Requirements:
 *  4.1  — Store documents securely in S3 (download via signed URL)
 *  4.6  — Generate unique identifier and store metadata in DynamoDB
 *  7.5  — Show verification status for each uploaded Document
 *  9.2  — Display warning when Document expires within 30 days
 * 11.1  — Categorize Documents as either crew-related or yacht-related
 * 11.6  — Separate crew requirements from yacht requirements in display
 * 12.4  — When a user deletes a Document, permanently remove it from S3
 * 14.4  — Auto-refresh compliance dashboard after upload
 */

/** Ref interface for external refresh trigger. */
export interface DocumentListPageRef {
  refresh: () => void;
}

/** Number of days before expiration to show a warning. */
const EXPIRATION_WARNING_DAYS = 30;

/** Compute days remaining until expiration. Returns null if no date. */
function daysUntilExpiration(expirationDate?: string): number | null {
  if (!expirationDate) return null;
  const now = new Date();
  const exp = new Date(expirationDate);
  const diffMs = exp.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/** Format document type for display */
function formatDocumentType(type: string): string {
  // Map old document types to new names
  const typeMapping: Record<string, string> = {
    'measurement_certificate': 'Rating Certificate',
  };
  
  if (typeMapping[type]) {
    return typeMapping[type];
  }
  
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format date for display */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
}

const DocumentListPage = forwardRef<DocumentListPageRef>((_, ref) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<{ url: string; type: string } | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docs, crew] = await Promise.all([
        documentService.getDocuments(),
        crewService.getCrew(),
      ]);
      setDocuments(docs);
      setCrewMembers(crew);
    } catch {
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: fetchData,
  }));

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Get crew member name by ID */
  const getCrewName = (crewId?: string): string => {
    if (!crewId) return '-';
    const crew = crewMembers.find((c) => c.crewId === crewId);
    return crew ? `${crew.firstName} ${crew.lastName}` : '-';
  };

  /** Crew documents have a crewId; yacht documents do not */
  const crewDocuments = documents.filter((d) => !!d.crewId);
  const yachtDocuments = documents.filter((d) => !d.crewId);

  /** View a document in a modal. */
  const handleView = async (documentId: string, s3Key: string, metadata: Record<string, unknown>) => {
    setViewLoading(true);
    try {
      const url = await documentService.getDownloadUrl(documentId);
      // Check filename from metadata or s3Key
      const filename = (metadata?.filename as string) || s3Key || '';
      const isPdf = filename.toLowerCase().endsWith('.pdf');
      setViewingDoc({ url, type: isPdf ? 'pdf' : 'image' });
    } catch {
      alert('Failed to load document. Please try again.');
    } finally {
      setViewLoading(false);
    }
  };

  /** Close the view modal. */
  const handleCloseView = () => {
    setViewingDoc(null);
  };

  /** Download a document via signed URL */
  const handleDownload = async (documentId: string) => {
    try {
      const url = await documentService.getDownloadUrl(documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      alert('Failed to download document. Please try again.');
    }
  };

  /** Delete a document with confirmation */
  const handleDelete = async (documentId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this document? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      await documentService.deleteDocument(documentId);
      setDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
    } catch {
      alert('Failed to delete document. Please try again.');
    }
  };

  /** Render expiry status with warning */
  const renderExpiryStatus = (doc: Document) => {
    const expiryDate = doc.expirationDate || (doc.metadata?.expiry_date as string);
    if (!expiryDate) return '-';
    
    const days = daysUntilExpiration(expiryDate);
    const isExpiringSoon = days !== null && days >= 0 && days <= EXPIRATION_WARNING_DAYS;
    const isExpired = days !== null && days < 0;

    return (
      <span className={isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : ''}>
        {formatDate(expiryDate)}
        {isExpired && ' ⚠️ Expired'}
        {isExpiringSoon && !isExpired && ` ⚠️ ${days}d`}
      </span>
    );
  };

  /** Render document table */
  const renderTable = (docs: Document[], title: string, showCrewColumn: boolean) => (
    <div className="document-section">
      <h2>{title}</h2>
      {docs.length === 0 ? (
        <p className="empty-section">No {title.toLowerCase()} found.</p>
      ) : (
        <div className="table-responsive">
          <table className="documents-table">
            <thead>
              <tr>
                {showCrewColumn && <th>Crew Member</th>}
                <th>Document Type</th>
                <th>Issuing Authority</th>
                <th>Issue Date</th>
                <th>Expiry Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => (
                <tr key={doc.documentId} data-testid={`document-${doc.documentId}`}>
                  {showCrewColumn && (
                    <td>{doc.metadata?.holder_name as string || getCrewName(doc.crewId)}</td>
                  )}
                  <td>{formatDocumentType(doc.documentType)}</td>
                  <td>{(doc.metadata?.issuer as string) || '-'}</td>
                  <td>{formatDate(doc.metadata?.issue_date as string)}</td>
                  <td>{renderExpiryStatus(doc)}</td>
                  <td className="document-actions">
                    <button
                      type="button"
                      className="view-btn"
                      onClick={() => handleView(doc.documentId, doc.s3Key, doc.metadata)}
                      disabled={viewLoading}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="download-btn"
                      onClick={() => handleDownload(doc.documentId)}
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      className="delete-btn"
                      onClick={() => handleDelete(doc.documentId)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="document-list-page">
      <h1>My Documents</h1>

      {loading && <p data-testid="loading-indicator">Loading documents…</p>}

      {error && (
        <p className="error-message" data-testid="error-message">
          {error}
        </p>
      )}

      {!loading && !error && documents.length === 0 && (
        <p data-testid="empty-state">No documents uploaded yet.</p>
      )}

      {!loading && !error && documents.length > 0 && (
        <div data-testid="document-list-content">
          {renderTable(crewDocuments, 'Crew Documents', true)}
          {renderTable(yachtDocuments, 'Yacht Documents', false)}
        </div>
      )}

      {/* Document View Modal */}
      {viewingDoc && (
        <div className="modal-overlay" data-testid="view-modal">
          <div className="modal-content document-viewer">
            <div className="modal-header">
              <h2>Document Viewer</h2>
              <button className="modal-close" onClick={handleCloseView} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              {viewingDoc.type === 'pdf' ? (
                <iframe
                  src={`${viewingDoc.url}#toolbar=1&navpanes=0`}
                  title="Document Viewer"
                  className="document-iframe"
                  data-testid="document-iframe"
                />
              ) : (
                <img
                  src={viewingDoc.url}
                  alt="Document"
                  className="document-image"
                  data-testid="document-image"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DocumentListPage;
